import React, { useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import * as client from "../../../client";
import { TermWordedValue } from "../policy/termWording";
import { RowBadge } from "./FieldRow";
import { ExcerptGroup, ExcerptRow } from "./excerptGroups";
import styles from "./OverviewPanes.module.css";

// Schedules longer than this start collapsed so the pane stays scannable.
const COLLAPSE_OVER = 8;

type Props<R extends ExcerptRow> = {
  title: string;
  hint: string;
  groups: ExcerptGroup<R>[];
  /** Groups a new row can be added to; a single target hides the picker. */
  addTargets: { label: string; value: string }[];
  addLabel: string;
  emptyText: string;
  /** Rows show their value as it prints for the term; edits bind to the stored text. */
  termWording: client.TermWording;
  onEdit: (group: ExcerptGroup<R>, row: R, name: string, value: string) => void;
  onRemove: (group: ExcerptGroup<R>, row: R) => void;
  onAdd: (groupKey: string, name: string, value: string) => void;
};

/**
 * A collapsible schedule of limit / sublimit / deductible rows grouped by
 * peril or classification, with inline edit, remove and add. Edits are
 * reported up and land in the submission draft; nothing saves here.
 */
export const ExcerptSection = <R extends ExcerptRow>({
  title,
  hint,
  groups,
  addTargets,
  addLabel,
  emptyText,
  termWording,
  onEdit,
  onRemove,
  onAdd,
}: Props<R>) => {
  const total = groups.reduce((sum, group) => sum + group.rows.length, 0);
  const [open, setOpen] = useState(total <= COLLAPSE_OVER);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editValue, setEditValue] = useState("");
  const [adding, setAdding] = useState(false);
  const [addTarget, setAddTarget] = useState<string | null>(null);
  const [addName, setAddName] = useState("");
  const [addValue, setAddValue] = useState("");

  const startEdit = (row: R) => {
    setEditingId(row.id);
    setEditName(row.name);
    setEditValue(row.value);
  };

  const commitEdit = (group: ExcerptGroup<R>, row: R) => {
    if (editName.trim() !== "" && editValue.trim() !== "") {
      onEdit(group, row, editName, editValue);
    }
    setEditingId(null);
  };

  const startAdd = () => {
    setAdding(true);
    setAddTarget(
      addTargets.length === 1 ? (addTargets[0]?.value ?? null) : null
    );
    setAddName("");
    setAddValue("");
  };

  const commitAdd = () => {
    if (addTarget && addName.trim() !== "" && addValue.trim() !== "") {
      onAdd(addTarget, addName.trim(), addValue);
      setAdding(false);
    }
  };

  const onFormKey = (e: React.KeyboardEvent, commit: () => void) => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") {
      setEditingId(null);
      setAdding(false);
    }
  };

  const renderRow = (group: ExcerptGroup<R>, row: R) =>
    editingId === row.id ? (
      <div key={row.id} className={styles.inlineForm}>
        <InputText
          autoFocus
          value={editName}
          placeholder="Name"
          onChange={(e) => setEditName(e.target.value)}
          onKeyDown={(e) => onFormKey(e, () => commitEdit(group, row))}
        />
        <InputText
          value={editValue}
          placeholder="Value"
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => onFormKey(e, () => commitEdit(group, row))}
        />
        <div className={styles.inlineButtons}>
          <Button
            icon="pi pi-check"
            rounded
            text
            size="small"
            type="button"
            onClick={() => commitEdit(group, row)}
          />
          <Button
            icon="pi pi-times"
            rounded
            text
            size="small"
            type="button"
            onClick={() => setEditingId(null)}
          />
        </div>
      </div>
    ) : (
      <div
        key={row.id}
        className={styles.excerptRow}
        onClick={() => startEdit(row)}
      >
        <span className={styles.excerptName}>{row.name}</span>
        <span className={styles.excerptValue}>
          <TermWordedValue termWording={termWording} stored={row.value} />
        </span>
        {row.manual && <RowBadge tone="custom">Manual</RowBadge>}
        <div className={styles.excerptActions}>
          <Button
            icon="pi pi-pencil"
            rounded
            text
            size="small"
            type="button"
            tooltip="Edit"
            tooltipOptions={{ showDelay: 400 }}
            onClick={(e) => {
              e.stopPropagation();
              startEdit(row);
            }}
          />
          <Button
            icon="pi pi-trash"
            rounded
            text
            size="small"
            severity="danger"
            type="button"
            tooltip="Remove from the report"
            tooltipOptions={{ showDelay: 400 }}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(group, row);
            }}
          />
        </div>
      </div>
    );

  return (
    <section className={styles.section}>
      <button
        type="button"
        className={`${styles.sectionTitle} ${styles.collapseHeader} ${
          open ? "" : styles.collapsed
        }`}
        onClick={() => setOpen((o) => !o)}
      >
        <i className={`pi pi-chevron-${open ? "down" : "right"}`} />
        <span>{title}</span>
        <span className={styles.sectionHint}>{hint}</span>
        <span className={styles.sectionCount}>{total}</span>
      </button>
      {open && (
        <div>
          {groups.map(
            (group) =>
              group.rows.length > 0 && (
                <div
                  key={group.key}
                  className={`${styles.group} ${
                    group.muted ? styles.groupMuted : ""
                  }`}
                >
                  <div className={styles.groupTitle}>
                    <span>{group.title}</span>
                    {group.note && (
                      <span className={styles.groupNote}>{group.note}</span>
                    )}
                  </div>
                  {group.rows.map((row) => renderRow(group, row))}
                </div>
              )
          )}
          {total === 0 && (
            <div className={styles.emptySection}>{emptyText}</div>
          )}
          {adding ? (
            <div
              className={`${styles.inlineForm} ${
                addTargets.length > 1 ? styles.inlineFormWithTarget : ""
              }`}
            >
              {addTargets.length > 1 && (
                <Dropdown
                  value={addTarget}
                  options={addTargets}
                  placeholder="Where"
                  onChange={(e) => setAddTarget(e.value)}
                />
              )}
              <InputText
                autoFocus
                value={addName}
                placeholder="Name"
                onChange={(e) => setAddName(e.target.value)}
                onKeyDown={(e) => onFormKey(e, commitAdd)}
              />
              <InputText
                value={addValue}
                placeholder="Value"
                onChange={(e) => setAddValue(e.target.value)}
                onKeyDown={(e) => onFormKey(e, commitAdd)}
              />
              <div className={styles.inlineButtons}>
                <Button
                  icon="pi pi-check"
                  rounded
                  text
                  size="small"
                  type="button"
                  disabled={
                    !addTarget ||
                    addName.trim() === "" ||
                    addValue.trim() === ""
                  }
                  onClick={commitAdd}
                />
                <Button
                  icon="pi pi-times"
                  rounded
                  text
                  size="small"
                  type="button"
                  onClick={() => setAdding(false)}
                />
              </div>
            </div>
          ) : (
            <button
              type="button"
              className={styles.addButton}
              onClick={startAdd}
            >
              <i className="pi pi-plus" /> {addLabel}
            </button>
          )}
        </div>
      )}
    </section>
  );
};
